import React, { useState, useEffect } from 'react';
import { API_ENDPOINTS } from '../../config/apiRouting';
import authenticatedFetch from '../../utils/authenticatedFetch';
import { FollowUpLogger } from './FollowUpLogger';
import './RecoveryResponseEditor.css';

interface Workflow {
  workflowId: string;
  storeId: string;
  platform: string;
  rating: number;
  reviewText: string;
  reviewerName: string;
  tier: number;
  status: string;
  complaints: string[];
  posContext: any;
  publicReply: string;
  privateMessage: string;
  offerType: string;
  offerValue: number;
  expirationDays: number;
  deliveryMethod?: string;
  recoveryCode: string;
  slaDeadline: string;
  snoozeCount: number;
  followUps: any[];
  events: any[];
  createdAt: string;
}

interface Props {
  workflowId: string;
  onClose: () => void;
  onRefresh: () => void;
}

const TIER_COLORS: { [key: number]: string } = { 1: '#3b82f6', 2: '#f59e0b', 3: '#ef4444', 4: '#7c3aed' };

export const RecoveryResponseEditor: React.FC<Props> = ({ workflowId, onClose, onRefresh }) => {
  const [workflow, setWorkflow] = useState<Workflow | null>(null);
  const [publicReply, setPublicReply] = useState('');
  const [privateMessage, setPrivateMessage] = useState('');
  const [selectedTier, setSelectedTier] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [showFollowUp, setShowFollowUp] = useState(false);
  const [replyOnlyReason, setReplyOnlyReason] = useState('');

  // ESC key to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  useEffect(() => {
    fetchWorkflow();
  }, [workflowId]);

  const fetchWorkflow = async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.recoveryWorkflow(workflowId));
      if (response.ok) {
        const data = await response.json();
        const wf = data.workflow;
        setWorkflow(wf);
        setPublicReply(wf.publicReply || '');
        setPrivateMessage(wf.privateMessage || '');
        setSelectedTier(wf.tier);
      }
    } catch (err) {
      console.error('Failed to fetch workflow:', err);
    } finally {
      setLoading(false);
    }
  };

  const performAction = async (action: string, endpoint: string, method: string, body: any) => {
    setActionLoading(action);
    setMessage(null);
    try {
      const response = await authenticatedFetch(endpoint, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      });
      const data = await response.json();
      if (response.ok) {
        setMessage(data.message || 'Action completed');
        onRefresh();
        if (['approve', 'reply-only', 'close'].includes(action)) {
          setTimeout(() => onClose(), 1500);
        } else {
          fetchWorkflow();
        }
      } else {
        setMessage(data.error || 'Action failed');
      }
    } catch (err) {
      setMessage('Error performing action');
    } finally {
      setActionLoading(null);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const handleApprove = () => performAction('approve', API_ENDPOINTS.recoveryApprove(workflowId), 'POST', { publicReply, privateMessage });
  const handleEdit = () => performAction('edit', API_ENDPOINTS.recoveryEdit(workflowId), 'PUT', { publicReply, privateMessage });
  const handleReplyOnly = () => performAction('reply-only', API_ENDPOINTS.recoveryReplyOnly(workflowId), 'POST', { publicReply, reason: replyOnlyReason });
  const handleSnooze = () => performAction('snooze', API_ENDPOINTS.recoverySnooze(workflowId), 'POST', { hours: 4 });
  const handleEscalate = () => performAction('escalate', API_ENDPOINTS.recoveryEscalate(workflowId), 'POST', { reason: 'Manager escalation' });
  const handleClose = () => performAction('close', API_ENDPOINTS.recoveryClose(workflowId), 'POST', { resolution: 'resolved' });
  const handlePostReply = () => performAction('post-reply', API_ENDPOINTS.recoveryApprove(workflowId), 'POST', { publicReply, privateMessage, postOnly: true });
  const handleSendSms = () => performAction('send-sms', API_ENDPOINTS.recoveryApprove(workflowId), 'POST', { privateMessage, deliveryMethod: 'sms' });
  const handleSendEmail = () => performAction('send-email', API_ENDPOINTS.recoveryApprove(workflowId), 'POST', { privateMessage, deliveryMethod: 'email' });

  const handleChangeTier = async () => {
    if (selectedTier === workflow?.tier) return;
    performAction('change-tier', API_ENDPOINTS.recoveryChangeTier(workflowId), 'POST', { tier: selectedTier });
  };

  if (loading) return <div className="editor-loading">Loading workflow...</div>;
  if (!workflow) return <div className="editor-error">Workflow not found</div>;

  const offerDesc = workflow.offerType === 'percentage' ? `${workflow.offerValue}% off` : `$${workflow.offerValue} off`;
  const isActionable = ['AI_READY', 'AUTO_APPROVED', 'OVERDUE'].includes(workflow.status);
  const isApproved = workflow.status === 'APPROVED';
  const isClosed = ['CLOSED', 'REPLY_ONLY'].includes(workflow.status);
  const isEscalated = workflow.status === 'ESCALATED';

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div className="recovery-editor-overlay" onClick={handleOverlayClick}>
      <div className="recovery-editor">
        <div className="editor-header">
          <div className="editor-title">
            <h3>Recovery Response Editor</h3>
            <span className="editor-workflow-id">#{workflowId.substring(0, 8)}</span>
            <span className={`editor-status editor-status--${workflow.status.toLowerCase()}`}>{workflow.status.replace(/_/g, ' ')}</span>
          </div>
          <button className="close-btn" onClick={onClose} title="Close (Esc)"><i className="fas fa-times"></i> Close</button>
        </div>

        {message && <div className={`editor-message ${message.includes('fail') || message.includes('Error') ? 'error' : 'success'}`}>{message}</div>}

        <div className="editor-review-summary">
          <div className="summary-row">
            <span className="tier-badge" style={{ backgroundColor: TIER_COLORS[workflow.tier] }}>Tier {workflow.tier}</span>
            <span className="rating-display">{'*'.repeat(workflow.rating)}/5</span>
            <span className="reviewer">{workflow.reviewerName || 'Anonymous'}</span>
            <span className="platform">{workflow.platform}</span>
          </div>
          <div className="review-text-full">{workflow.reviewText || '(No text)'}</div>
          {workflow.complaints.length > 0 && (
            <div className="complaint-pills">
              {workflow.complaints.map(c => <span key={c} className="pill">{c.replace(/_/g, ' ')}</span>)}
            </div>
          )}
        </div>

        {!isClosed && (
          <div className="editor-tier-selector">
            <label>Tier Override:</label>
            <select value={selectedTier} onChange={e => setSelectedTier(parseInt(e.target.value))}>
              <option value={1}>Tier 1 - Minor</option>
              <option value={2}>Tier 2 - Moderate</option>
              <option value={3}>Tier 3 - Severe</option>
              <option value={4}>Tier 4 - Safety</option>
            </select>
            {selectedTier !== workflow.tier && (
              <button className="regen-btn" onClick={handleChangeTier} disabled={actionLoading === 'change-tier'}>
                {actionLoading === 'change-tier' ? 'Regenerating...' : 'Regenerate AI Response'}
              </button>
            )}
          </div>
        )}

        <div className="editor-offer-summary">
          <i className="fas fa-gift"></i>
          <span>Recovery Offer: <strong>{offerDesc}</strong> | Expires in {workflow.expirationDays} days</span>
          {workflow.recoveryCode && <span className="code-display">Code: {workflow.recoveryCode}</span>}
        </div>

        <div className="editor-responses">
          <div className="response-panel">
            <h4>Public Reply</h4>
            <p className="panel-desc">Posted publicly on the review platform</p>
            <textarea
              value={publicReply}
              onChange={e => setPublicReply(e.target.value)}
              disabled={isClosed}
              rows={8}
            />
          </div>
          <div className="response-panel">
            <h4>Private Recovery Message</h4>
            <p className="panel-desc">Sent directly to the guest via {workflow.deliveryMethod || 'email'}</p>
            <textarea
              value={privateMessage}
              onChange={e => setPrivateMessage(e.target.value)}
              disabled={isClosed}
              rows={8}
            />
          </div>
        </div>

        {isActionable && (
          <div className="editor-actions">
            <button className="action-btn approve" onClick={handleApprove} disabled={!!actionLoading}>
              <i className="fas fa-check"></i> {actionLoading === 'approve' ? 'Sending...' : 'Approve & Send All'}
            </button>
            <button className="action-btn edit" onClick={handleEdit} disabled={!!actionLoading}>
              <i className="fas fa-save"></i> {actionLoading === 'edit' ? 'Saving...' : 'Save Edits'}
            </button>
            <button className="action-btn post-reply" onClick={handlePostReply} disabled={!!actionLoading}>
              <i className="fas fa-reply"></i> {actionLoading === 'post-reply' ? 'Posting...' : 'Post Reply Only'}
            </button>
            <button className="action-btn send-sms" onClick={handleSendSms} disabled={!!actionLoading}>
              <i className="fas fa-sms"></i> {actionLoading === 'send-sms' ? 'Sending...' : 'Send SMS'}
            </button>
            <button className="action-btn send-email" onClick={handleSendEmail} disabled={!!actionLoading}>
              <i className="fas fa-envelope"></i> {actionLoading === 'send-email' ? 'Sending...' : 'Send Email'}
            </button>
          </div>
        )}

        {isActionable && (
          <div className="editor-secondary-actions">
            <div className="reply-only-group">
              <input type="text" placeholder="Reason for reply-only..." value={replyOnlyReason} onChange={e => setReplyOnlyReason(e.target.value)} />
              <button className="action-btn reply-only" onClick={handleReplyOnly} disabled={!!actionLoading || !replyOnlyReason}>
                Reply Only (No Recovery)
              </button>
            </div>
            <button className="action-btn snooze" onClick={handleSnooze} disabled={!!actionLoading || workflow.snoozeCount >= 2}>
              <i className="fas fa-clock"></i> Snooze ({2 - workflow.snoozeCount} left)
            </button>
            <button className="action-btn escalate" onClick={handleEscalate} disabled={!!actionLoading}>
              <i className="fas fa-arrow-up"></i> Escalate
            </button>
          </div>
        )}

        {isApproved && (
          <div className="editor-post-actions">
            <button className="action-btn post-reply" onClick={handlePostReply} disabled={!!actionLoading}>
              <i className="fas fa-reply"></i> {actionLoading === 'post-reply' ? 'Posting...' : 'Re-Post Reply'}
            </button>
            <button className="action-btn send-sms" onClick={handleSendSms} disabled={!!actionLoading}>
              <i className="fas fa-sms"></i> {actionLoading === 'send-sms' ? 'Sending...' : 'Re-Send SMS'}
            </button>
            <button className="action-btn send-email" onClick={handleSendEmail} disabled={!!actionLoading}>
              <i className="fas fa-envelope"></i> {actionLoading === 'send-email' ? 'Sending...' : 'Re-Send Email'}
            </button>
            <button className="action-btn follow-up" onClick={() => setShowFollowUp(!showFollowUp)}>
              <i className="fas fa-phone"></i> Log Follow-Up
            </button>
            <button className="action-btn close-workflow" onClick={handleClose} disabled={!!actionLoading}>
              <i className="fas fa-check-circle"></i> {actionLoading === 'close' ? 'Closing...' : 'Close Workflow'}
            </button>
          </div>
        )}

        {isEscalated && (
          <div className="editor-post-actions">
            <button className="action-btn approve" onClick={handleApprove} disabled={!!actionLoading}>
              <i className="fas fa-check"></i> {actionLoading === 'approve' ? 'Sending...' : 'Approve & Send'}
            </button>
            <button className="action-btn close-workflow" onClick={handleClose} disabled={!!actionLoading}>
              <i className="fas fa-check-circle"></i> Close Workflow
            </button>
          </div>
        )}

        {showFollowUp && (
          <FollowUpLogger workflowId={workflowId} onSaved={() => { setShowFollowUp(false); fetchWorkflow(); }} />
        )}

        {workflow.events && workflow.events.length > 0 && (
          <div className="editor-timeline">
            <h4>Timeline</h4>
            {workflow.events.slice().reverse().map((evt, i) => (
              <div key={i} className="timeline-event">
                <span className="event-time">{new Date(evt.timestamp).toLocaleString()}</span>
                <span className="event-type">{evt.type}</span>
                <span className="event-detail">{evt.detail}</span>
              </div>
            ))}
          </div>
        )}

        <div className="editor-footer">
          <button className="action-btn dismiss" onClick={onClose}>
            <i className="fas fa-times"></i> Close Editor
          </button>
        </div>
      </div>
    </div>
  );
};
